import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { quickBooksIntegrationCallback } from "@/services/integration.service";
import { startInitialSync } from "@/services/sync.quickbooks.service";
import posthog from "@/lib/posthog";

const QuickBooksCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasStartedRef = useRef(false);

  // Handle error params in URL
  useEffect(() => {
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    if (error) {
      const errorParams = new URLSearchParams();
      errorParams.set("error", error);
      if (errorDescription) {
        errorParams.set("error_description", errorDescription);
      }
      navigate(`/auth/error?${errorParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // Process the callback and redirect to dashboard
  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const processCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const realmId = searchParams.get("realmId");

      if (!code || !state || !realmId) {
        const errorParams = new URLSearchParams();
        errorParams.set("error", "missing_parameters");
        errorParams.set(
          "error_description",
          "Missing required QuickBooks callback parameters"
        );
        navigate(`/auth/error?${errorParams.toString()}`, { replace: true });
        return;
      }

      const expectedState = sessionStorage.getItem('qb_oauth_state');
      sessionStorage.removeItem('qb_oauth_state');
      if (!expectedState || state !== expectedState) {
        const errorParams = new URLSearchParams();
        errorParams.set("error", "invalid_state");
        errorParams.set("error_description", "OAuth state mismatch. Please try again.");
        navigate(`/auth/error?${errorParams.toString()}`, { replace: true });
        return;
      }

      try {
        await quickBooksIntegrationCallback();
        posthog.capture("quickbooks_connected", { realm_id: realmId });
        const syncStatusId = await startInitialSync();
        navigate("/onboarding/syncing", { replace: true, state: { fromCallback: true, syncStatusId } });
      } catch (error) {
        console.error("QuickBooks integration failed:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to connect QuickBooks";
        posthog.capture("quickbooks_connect_failed", { error_message: errorMessage });
        navigate(
          `/onboarding/connect?integration=error&message=${encodeURIComponent(errorMessage)}`,
          { replace: true }
        );
      }
    };

    processCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="flex items-center gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Connecting...</span>
      </div>
    </div>
  );
};

export default QuickBooksCallback;
